import React, { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LangContext";
import { CheckCircle2, RefreshCw } from "lucide-react";
import { Link } from "wouter";

export default function Precios() {
  const { tr } = useLang();
  const [isAnnual, setIsAnnual] = useState(true);

  const plans = [
    {
      id: "onepage",
      name: tr.pricing.onepage,
      monthlyPrice: "25",
      annualPrice: "250",
      subtitle: isAnnual
        ? "Pago único — incluye hosting y dominio 1 año"
        : "Facturación mensual",
      features: [
        "Landing page optimizada",
        "Diseño responsivo y moderno",
        "Formulario de contacto",
        "Mapa y datos de contacto",
        "Hosting y dominio incluidos",
        "SEO básico",
      ],
      renewalNote: true,
      highlight: false,
      ctaLabel: tr.nav.startProject,
    },
    {
      id: "multipages",
      name: tr.pricing.multipages,
      subtitle: isAnnual
        ? `${tr.pricing.multipagesDesc} — Pago único con hosting y dominio 1 año`
        : tr.pricing.multipagesDesc,
      monthlyPrice: "40",
      annualPrice: "400",
      features: [
        "Sitio corporativo completo",
        "Hasta 5 páginas internas",
        "SEO básico incluido",
        "Integración con Analytics",
        "Dashboard de gestión",
        "Correos empresariales",
        "Hosting y dominio incluidos",
      ],
      renewalNote: true,
      highlight: true,
      ctaLabel: tr.nav.startProject,
    },
    {
      id: "custom",
      name: tr.pricing.custom,
      subtitle: tr.pricing.customDesc,
      monthlyPrice: null,
      annualPrice: null,
      features: [
        "Tienda virtual completa (E-commerce)",
        "Diseño de identidad corporativa",
        "Estrategia de marketing digital",
        "Integración de APIs y sistemas",
        "Soporte y mantenimiento",
        "Cotización personalizada",
      ],
      renewalNote: false,
      highlight: false,
      ctaLabel: tr.pricing.contactQuote,
    },
  ];

  return (
    <div className="bg-background text-foreground overflow-x-hidden selection:bg-primary/30">
      {/* Hero Header */}
      <section className="relative pt-40 pb-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/hero-abstract.png"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-[#0a0f1e]/85" />
          <div className="absolute inset-0 dark-texture" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter mb-6 text-white">
              {tr.pricing.title}
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 font-light leading-relaxed max-w-2xl mx-auto">
              {tr.pricing.desc}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Toggle + Plans */}
      <section className="py-24 px-6 bg-slate-50 border-y border-slate-200 text-slate-900">
        <div className="max-w-7xl mx-auto">
          {/* Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center mb-16 gap-4"
          >
            <div className="inline-flex items-center p-1 bg-slate-200 rounded-full">
              <button
                onClick={() => setIsAnnual(false)}
                className={`px-7 py-2.5 rounded-full text-sm font-semibold transition-all ${!isAnnual ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
              >
                {tr.pricing.monthly}
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`px-7 py-2.5 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${isAnnual ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
              >
                {tr.pricing.annual}
                <span className="bg-primary/10 text-primary text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold">
                  {tr.pricing.save}
                </span>
              </button>
            </div>
            {isAnnual && (
              <motion.p
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-slate-500 font-medium"
              >
                El plan anual es un <strong>único pago</strong> que incluye
                hosting y dominio por 1 año completo.
              </motion.p>
            )}
          </motion.div>

          {/* Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto items-start">
            {plans.map((plan, i) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`rounded-3xl flex flex-col relative ${
                  plan.highlight
                    ? "bg-slate-900 border-2 border-primary shadow-xl text-white md:-translate-y-4"
                    : "bg-white border border-slate-200 shadow-sm text-slate-900"
                }`}
              >
                {plan.highlight && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-white text-xs font-bold uppercase tracking-widest py-1.5 px-4 rounded-full">
                    {tr.pricing.popular}
                  </div>
                )}

                <div className="p-8">
                  <h3
                    className={`text-xl font-bold mb-1 ${plan.highlight ? "text-white" : "text-slate-900"}`}
                  >
                    {plan.name}
                  </h3>
                  <p
                    className={`text-sm mb-6 min-h-[36px] ${plan.highlight ? "text-slate-400" : "text-slate-500"}`}
                  >
                    {plan.subtitle}
                  </p>

                  {plan.monthlyPrice ? (
                    <div className="flex items-baseline gap-1 mb-8">
                      <span
                        className={`text-4xl font-extrabold ${plan.highlight ? "text-white" : "text-slate-900"}`}
                      >
                        ${isAnnual ? plan.annualPrice : plan.monthlyPrice}
                      </span>
                      <span
                        className={`font-medium ${plan.highlight ? "text-slate-400" : "text-slate-500"}`}
                      >
                        USD{isAnnual ? " pago único" : tr.pricing.month}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-1 mb-8">
                      <span
                        className={`text-3xl font-extrabold ${plan.highlight ? "text-white" : "text-slate-800"}`}
                      >
                        A Medida
                      </span>
                    </div>
                  )}

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <CheckCircle2
                          className={`w-5 h-5 shrink-0 mt-0.5 ${plan.highlight ? "text-primary" : "text-primary"}`}
                        />
                        <span
                          className={`text-sm ${plan.highlight ? "text-slate-300" : "text-slate-600"}`}
                        >
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Annual renewal note */}
                  {plan.renewalNote && isAnnual && (
                    <div
                      className={`flex items-center gap-2 text-xs rounded-xl px-3 py-2 mb-6 ${plan.highlight ? "bg-white/10 text-slate-300" : "bg-slate-50 border border-slate-200 text-slate-500"}`}
                    >
                      <RefreshCw className="w-3.5 h-3.5 shrink-0" />
                      <span>
                        Renovación anual: <strong>$245 USD</strong>
                      </span>
                    </div>
                  )}
                </div>

                <div className="px-8 pb-8 mt-auto">
                  <Link
                    href="/contacto"
                    className={`inline-flex items-center justify-center rounded-xl text-sm font-bold h-12 w-full transition-colors ${
                      plan.highlight
                        ? "bg-primary text-white hover:bg-primary/90 shadow-[0_0_20px_rgba(56,189,248,0.3)]"
                        : plan.id === "custom"
                          ? "bg-slate-900 text-white hover:bg-slate-800"
                          : "bg-slate-100 text-slate-900 hover:bg-slate-200"
                    }`}
                  >
                    {plan.ctaLabel}
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Footer note */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-slate-500 text-sm mt-12"
          >
            ¿Tienes dudas sobre qué plan elegir?{" "}
            <Link
              href="/contacto"
              className="text-primary font-semibold hover:underline"
            >
              Escríbenos
            </Link>{" "}
            y te orientamos sin compromiso.
          </motion.p>
        </div>
      </section>

      {/* FAQ / Info Section (Dark) */}
      <section
        className="py-32 px-6 text-foreground relative overflow-hidden dark-texture"
        style={{
          background:
            "linear-gradient(150deg, #0d2137 0%, #0a0f1e 55%, #060c18 100%)",
        }}
      >
        <div className="max-w-4xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-extrabold text-white mb-4 tracking-tight">
              Preguntas Frecuentes
            </h2>
            <p className="text-muted-foreground text-lg">
              Todo lo que necesitas saber sobre nuestros planes.
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                q: "¿Qué incluye el hosting y dominio?",
                a: "Todos los planes anuales incluyen alojamiento web (hosting) y nombre de dominio (.com o .net) por 1 año completo, sin costos adicionales ocultos.",
              },
              {
                q: "¿Cuánto cuesta la renovación anual?",
                a: "La renovación anual de cualquier plan tiene un costo de $160 USD e incluye el mantenimiento del hosting y el dominio por un año más.",
              },
              {
                q: "¿Puedo cambiar de plan más adelante?",
                a: "Sí, en cualquier momento puedes hacer upgrade a un plan superior. Solo pagas la diferencia proporcional.",
              },
              {
                q: "¿Cuánto tiempo tarda el desarrollo?",
                a: "El Plan Onepage Express puede estar listo en 5-7 días hábiles. El Plan Multipáginas entre 10-15 días. Los proyectos personalizados dependen del alcance y se definen en la propuesta.",
              },
              {
                q: "¿Puedo pagar en cuotas?",
                a: "Sí, ofrecemos opciones de pago flexibles. Contáctanos y encontramos la mejor forma de adaptarnos a tu presupuesto.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="p-6 rounded-2xl bg-secondary/50 border border-border"
              >
                <h4 className="text-white font-semibold mb-2">{item.q}</h4>
                <p className="text-muted-foreground font-light leading-relaxed text-sm">
                  {item.a}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
